import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface Comment {
  id: string;
  media_id: string;
  user_id: string;
  content: string;
  created_at: string;
  updated_at: string;
  display_name?: string;
  avatar_url?: string;
}

export const useComments = (mediaId: string | null) => {
  const { user } = useAuth();

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch comments for a media item
  const fetchComments = useCallback(async () => {
    if (!mediaId) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('media_id', mediaId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch user info for all comments
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(c => c.user_id))];
        const { data: userInfo, error: userError } = await supabase.rpc(
          'get_user_display_info',
          { user_ids: userIds }
        );

        if (!userError && userInfo) {
          const userMap = userInfo.reduce((acc: Record<string, any>, u: any) => {
            acc[u.id] = u;
            return acc;
          }, {});

          const commentsWithUsers = data.map(comment => ({
            ...comment,
            display_name: userMap[comment.user_id]?.display_name || 'Usuario anónimo',
            avatar_url: userMap[comment.user_id]?.avatar_url,
          }));

          setComments(commentsWithUsers);
        } else {
          setComments(data);
        }
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  }, [mediaId]);

  // Add a new comment
  const addComment = async (content: string) => {
    if (!user || !mediaId) return;

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          media_id: mediaId,
          user_id: user.id,
          content: content.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      // Optimistic update so the new comment appears immediately
      const optimistic = {
        ...data,
        display_name: (user.user_metadata as any)?.full_name || 'Vos',
        avatar_url: (user.user_metadata as any)?.avatar_url || (user.user_metadata as any)?.picture || undefined,
      } as Comment;
      setComments((prev) => [...prev, optimistic]);

      return data;
    } catch (error) {
      console.error('Error adding comment:', error);
      throw error;
    }
  };

  // Edit a comment
  const editComment = async (commentId: string, content: string) => {
    try {
      const { error } = await supabase
        .from('comments')
        .update({ content: content.trim() })
        .eq('id', commentId)
        .eq('user_id', user?.id);

      if (error) throw error;

      // Optimistic local update
      setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, content: content.trim() } : c)));

    } catch (error) {
      console.error('Error editing comment:', error);
      throw error;
    }
  };

  // Delete a comment
  const deleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) throw error;

      // Optimistic local removal
      setComments((prev) => prev.filter((c) => c.id !== commentId));

    } catch (error) {
      console.error('Error deleting comment:', error);
      throw error;
    }
  };

  // Subscribe to real-time updates
  useEffect(() => {
    if (!mediaId) return;

    fetchComments();

    const channel = supabase
      .channel(`comments-${mediaId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'comments',
          filter: `media_id=eq.${mediaId}`,
        },
        () => {
          fetchComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [mediaId, fetchComments]);

  return {
    comments,
    loading,
    addComment,
    editComment,
    deleteComment,
    refetch: fetchComments,
  };
};
